'use client';

import { createTheme } from '@mui/material';

const palette = {
	primary: {
		main: '#004BB9',
		100: '#E5EFFD',
		700: '#012A74',
	},
	secondary: {
		main: '#0061EF',
		100: '#E5EFFD',
		500: '#0061EF',
	},
	neutrals: {
		50: '#F4F6F9',
		100: '#E9E9EA',
		300: '#D2D3D3',
		500: '#8A8988',
		700: '#4A4A4A',
		900: '#202225',
	},
	success: {
		main: '#027D5E',
	},
	warning: {
		main: '#f8b622',
		100: '#FFF4CC',
		900: '#b89345',
	},
	error: {
		main: '#D0292A',
		100: '#FEEAEA',
		900: '#A61F1A',
	},
	action: {
		disabled: '#D2D3D3',
		disabledBackground: '#F4F6F9',
	},
};

export const theme = createTheme({
	palette,
	typography: {
		fontFamily: '"Avenir", sans-serif',
		button: {
			fontFamily: 'Avenir',
			fontSize: '18px',
			fontStyle: 'normal',
			fontWeight: 500,
			lineHeight: '22px',
			textAlign: 'center',
		},
		h1: {
			fontSize: '40px',
			fontStyle: 'normal',
			fontWeight: 800,
			lineHeight: '52px',
		},
		titleContainer: {
			fontFamily: 'Avenir',
			fontSize: '40px',
			fontStyle: 'normal',
			fontWeight: 800,
			lineHeight: '52px',
			color: palette.primary[700],
		},
		benefitsTitle: {
			color: '#012A74',
			fontSize: '28px',
			fontStyle: 'normal',
			fontWeight: 800,
			lineHeight: '40px',
			textAlign: 'center',

			'@media (max-width: 900px)': {
				textAlign: 'left',
				fontSize: '24px',
				lineHeight: '32px',
			},
		},
		h2: {
			fontSize: '32px',
			fontStyle: 'normal',
			fontWeight: 800,
			lineHeight: '40px',
		},
		h3: {
			fontSize: '24px',
			fontStyle: 'normal',
			fontWeight: 800,
			lineHeight: '32px',
		},
		bodyLarge: {
			fontSize: '18px',
			fontStyle: 'normal',
			fontWeight: 350,
			lineHeight: '22px',
		},
		titleDescription: {
			fontSize: '18px',
			fontStyle: 'normal',
			fontWeight: 350,
			lineHeight: '22px',
			color: palette.primary[700],
		},
		inputLabel: {
			fontFamily: 'Avenir',
			fontSize: '18px',
			fontWeight: 800,
			lineHeight: '22px',
			color: palette.primary[700],
		},
		bodySmall: {
			fontSize: '14px',
			fontStyle: 'normal',
			fontWeight: 800,
			lineHeight: '20px',
		},
		headingBenefit: {
			fontSize: '20px',
			fontStyle: 'normal',
			fontWeight: 800,
			lineHeight: '26px',
			textAlign: 'center',
			color: palette.primary[700],
		},
		heading: {
			fontSize: '20px',
			fontStyle: 'normal',
			fontWeight: 800,
			lineHeight: '26px',
			textAlign: 'center',
		},
		subtext: {
			fontSize: '18px',
			fontStyle: 'normal',
			fontWeight: 500,
			lineHeight: '26px',
			textAlign: 'center',
		},
		mapLink: {
			fontFamily: 'Avenir',
			fontSize: '16px',
			fontWeight: 500,
			lineHeight: '22px',
			textAlign: 'left',
			color: palette.secondary.main,
		},
		modalHeader: {
			fontFamily: 'Avenir',
			fontSize: '16px',
			fontWeight: 800,
			lineHeight: '22px',
			textAlign: 'left',
			color: '#013591',
		},
		descriptionBenefit: {
			fontSize: '18px',
			fontStyle: 'normal',
			fontWeight: 500,
			lineHeight: '26px',
			textAlign: 'center',
			color: palette.neutrals[700],
		},
		xSmall: {
			fontSize: '12px',
			fontStyle: 'normal',
			fontWeight: 500,
			lineHeight: '20px',
		},
		tag: {
			fontSize: '14px',
			fontStyle: 'normal',
			fontWeight: 500,
			lineHeight: '20px',
			textAlign: 'center',
		},
		cardContent: {
			fontFamily: 'Avenir',
			fontSize: '14px',
			fontStyle: 'normal',
			fontWeight: 350,
			lineHeight: '20px',
		},
		label: {
			fontSize: '12px',
			fontWeight: 500,
			lineHeight: '16px',
			textAlign: 'center',
			backgroundColor: palette.primary[100],
			padding: '4px 8px',
			borderRadius: '4px',
		},
		editLocation: {
			fontSize: '12px',
			fontStyle: 'normal',
			fontWeight: 350,
			lineHeight: '16px',
			color: palette.primary[600],
			cursor: 'pointer',
		},
	},
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					display: 'flex',
					padding: '10px 16px',
					justifyContent: 'center',
					alignItems: 'center',
					gap: '10px',
					borderRadius: '20px',
					textTransform: 'none',
					fontFamily: 'Avenir, sans-serif',
					fontWeight: 500,
					fontSize: '18px',
					lineHeight: '22px',
					textAlign: 'center',
					'&.Mui-disabled': {
						backgroundColor: '#F4F6F9',
						color: '#D2D3D3',
					},
				},
			},
			variants: [
				{
					props: { variant: 'primary' },
					style: {
						backgroundColor: '#004BB9',
						color: '#FFF',
						padding: '10px 20px',
						borderRadius: '20px',
						'&:hover': {
							backgroundColor: '#E5EFFD',
							color: '#004BB9',
						},
						'&:active': {
							backgroundColor: '#004EBF',
							color: '#FFF',
						},
					},
				},
				{
					props: { variant: 'secondary' },
					style: {
						backgroundColor: '#FFF',
						border: '1px solid #004BB9',
						color: '#004BB9',
						padding: '10px 20px',
						borderRadius: '20px',
						'&:hover': {
							backgroundColor: '#E5EFFD',
						},
						'&:active': {
							backgroundColor: '#D1E3FB',
						},
						'&.Mui-disabled': {
							backgroundColor: '#F4F6F9',
							color: '#D2D3D3',
							cursor: 'auto',
						},
					},
				},
				{
					props: { variant: 'dark' },
					style: {
						backgroundColor: '#FFF',
						color: '#004BB9',
						'&:hover': {
							backgroundColor: '#E5EFFD',
						},
						'&:active': {
							backgroundColor: '#012A74',
							color: '#FFF',
						},
						'&.Mui-disabled': {
							backgroundColor: '#F4F6F9',
							borderColor: '#D2D3D3',
							color: '#D2D3D3',
							cursor: 'auto',
						},
					},
				},
				{
					props: { variant: 'ghost' },
					style: {
						backgroundColor: 'transparent',
						border: 'none',
						color: 'inherit',
						padding: '0',
						'&:hover': {
							backgroundColor: 'transparent',
						},
						'&:active': {
							backgroundColor: 'transparent',
						},
					},
				},
			],
		},
	},
});

export default theme;

declare module '@mui/material/styles/createTheme' {
	interface Theme {
		brand: {
			primary: string;
			secondary: string;
		};
	}

	interface ThemeOptions {
		brand?: {
			primary?: string;
			secondary?: string;
		};
	}
}

declare module '@mui/material/styles' {
	interface TypographyVariants {
		tag: React.CSSProperties;
		label: React.CSSProperties;
		subtext: React.CSSProperties;
		heading: React.CSSProperties;
		bodyLarge?: React.CSSProperties;
		bodySmall?: React.CSSProperties;
		xSmall?: React.CSSProperties;
		cardContent: React.CSSProperties;
		mapLink: React.CSSProperties;
		modalHeader: React.CSSProperties;
		inputLabel?: React.CSSProperties;
		headingBenefit?: React.CSSProperties;
		descriptionBenefit?: React.CSSProperties;
		titleContainer?: React.CSSProperties;
		titleDescription?: React.CSSProperties;
		title?: React.CSSProperties | MediaQueryStyle;
		description?: React.CSSProperties;
		benefitsTitle?: React.CSSProperties | MediaQueryStyle;
		editLocation?: React.CSSProperties;
	}

	interface TypographyVariantsOptions {
		tag?: React.CSSProperties;
		label?: React.CSSProperties;
		subtext?: React.CSSProperties;
		heading?: React.CSSProperties;
		bodyLarge?: React.CSSProperties;
		bodySmall?: React.CSSProperties;
		xSmall?: React.CSSProperties;
		cardContent?: React.CSSProperties;
		mapLink?: React.CSSProperties;
		modalHeader?: React.CSSProperties;
		inputLabel?: React.CSSProperties;
		headingBenefit?: React.CSSProperties;
		descriptionBenefit?: React.CSSProperties;
		titleContainer?: React.CSSProperties;
		titleDescription?: React.CSSProperties;
		title?: React.CSSProperties | MediaQueryStyle;
		description?: React.CSSProperties;
		editLocation?: React.CSSProperties;
		benefitsTitle?: React.CSSProperties | MediaQueryStyle;
	}
}

declare module '@mui/material/Button' {
	interface ButtonPropsVariantOverrides {
		primary: true;
		secondary: true;
		dark: true;
		ghost: true;
	}
}

declare module '@mui/material/styles/createPalette' {
	interface Palette {
		neutrals: {
			50: string;
			100: string;
			300: string;
			500: string;
			700: string;
			900: string;
		};
	}

	interface PaletteOptions {
		neutrals?: {
			50: string;
			100: string;
			300: string;
			500: string;
			700: string;
			900: string;
		};
	}
}

declare module '@mui/material/Typography' {
	interface TypographyPropsVariantOverrides {
		tag: true;
		label: true;
		subtext: true;
		heading: true;
		bodyLarge: true;
		bodySmall: true;
		xSmall: true;
		cardContent: true;
		mapLink: true;
		modalHeader: true;
		inputLabel: true;
		headingBenefit: true;
		descriptionBenefit: true;
		titleContainer: true;
		titleDescription: true;
		title: true;
		description: true;
		benefitsTitle: true;
		editLocation: true;
	}
}

interface MediaQueryStyle {
	[key: string]: React.CSSProperties | MediaQueryStyle;
}
